// @ts-nocheck
import { api } from "../api/client";
import { uploadPetPhoto } from "../api/uploads";
import {
  getCurrentUser,
  refreshSession,
  resendRegisterOtp,
  startRegister,
  verifyRegisterOtp,
} from "../auth/session";
import { compressImageToBlob } from "../utils/image";
import { mapState } from "../map/state";
import {
  clearPetFormFields,
  readPetFormFields,
  setupGravePetFormBindings,
  validatePetDates,
} from "../utils/pet-form";
import { showToast } from "../components/toast/toast";
import { t } from "../i18n";
import { translateApiError, translateApiMessage } from "../i18n/api-errors";
import { trackMetaEvent } from "../utils/meta-events";
import { META_CHECKOUT_CURRENCY, META_CHECKOUT_VALUE } from "../utils/meta-checkout-value";
import { showView, getViewContainer } from "./view-router";
import { dismissOnboarding } from "./onboarding";
import { openMemorialThanks } from "./payment-thanks";
import { allocateEmptyHole } from "../map/find-empty";
import { speciesOptionsHtml } from "../utils/breeds";
import {
  renderCreaPreviewPhoto,
  renderUploadEmptyHtml,
  updateCreaPhotoUpload,
} from "../utils/pet-photo-placeholder";
import { applyHeadstone } from "../map/data-sync";
import { HEADSTONE_STYLE_KEYS, styleLabelKey } from "../utils/headstone-styles";
import { handleEntitlementError, isPethernityActive, openPaywall } from "./pethernity";

let graveFormReady = false;
let creaViewMounted = false;
let pendingCreaRegisterEmail: string | null = null;

export function resetGraveFormBindings() {
  graveFormReady = false;
}

function ensureGraveFormReady() {
  if (graveFormReady) return;
  setupGravePetFormBindings();
  graveFormReady = true;
}

// Builds the full-screen "Crea un memoriale" view once, preserving the grave-*
// field ids so readPetFormFields('grave') / setupGravePetFormBindings work
// unchanged. Style picker is gated: free users keep `standard`.
function ensureCreaView() {
  const host = getViewContainer("crea");
  if (!host || creaViewMounted) return;

  host.innerHTML = /* html */ `
    <div class="crea-inner">
      <header class="view-topbar">
        <button type="button" class="back-chip" onclick="closeModal()" aria-label="${t("myHeadstones.backToGarden")}">
          <i data-lucide="arrow-left"></i><span>${t("myHeadstones.backToGarden")}</span>
        </button>
      </header>
      <div class="view-head">
        <div class="eyebrow">Nuovo memoriale</div>
        <h1 class="view-title">Un nuovo memoriale</h1>
        <p class="view-subtitle">Crea un luogo eterno per chi ami</p>
      </div>
      <div class="crea-body pscroll">
      <form class="crea-form" id="crea-form" onsubmit="return false;">
        <div id="crea-account-block" class="crea-account" hidden>
          <div class="crea-account__head">
            <div class="eyebrow">${t("grave.accountEyebrow")}</div>
            <p class="crea-account__note">${t("grave.accountNote")}</p>
          </div>
          <div class="crea-field">
            <label class="field-label" for="crea-email">${t("auth.email")} <span class="req">*</span></label>
            <input type="email" id="crea-email" class="field-input" autocomplete="email">
          </div>
          <div class="crea-field">
            <label class="field-label" for="crea-password">${t("auth.passwordPlaceholder")} <span class="req">*</span></label>
            <input type="password" id="crea-password" class="field-input" autocomplete="new-password" minlength="6">
          </div>
        </div>
        <div class="crea-field">
          <label class="field-label" for="grave-name">Nome dell'animale <span class="req">*</span></label>
          <input type="text" id="grave-name" class="field-input" placeholder="${t("grave.animalName")}" maxlength="30" required>
        </div>

        <div class="crea-row">
          <div class="crea-field">
            <label class="field-label" for="grave-species">Tipo</label>
            <select id="grave-species" class="field-input">
              <option value="">${t("grave.speciesPlaceholder")}</option>
              ${speciesOptionsHtml()}
            </select>
            <div id="grave-species-other-wrap" style="display:none; margin-top:8px;">
              <input type="text" id="grave-species-other" class="field-input" placeholder="${t("grave.speciesOther")}" maxlength="80">
            </div>
          </div>
          <div class="crea-field">
            <label class="field-label" for="grave-breed">Razza</label>
            <input type="text" id="grave-breed" class="field-input" placeholder="${t("grave.breed")}" maxlength="80" autocomplete="off">
          </div>
        </div>

        <div class="crea-row">
          <div class="crea-field">
            <label class="field-label" for="grave-birth">Nascita</label>
            <input type="date" id="grave-birth" class="field-input">
          </div>
          <div class="crea-field">
            <label class="field-label" for="grave-death">Commiato</label>
            <input type="date" id="grave-death" class="field-input">
          </div>
        </div>

        <div class="crea-field">
          <label class="field-label" for="grave-text">Epitaffio <span class="req">*</span></label>
          <textarea id="grave-text" class="field-input" rows="3" placeholder="${t("grave.epitaph")}" maxlength="100" required></textarea>
        </div>

        <div class="crea-field">
          <label class="field-label">${t("pethernity.stylesLabel")}</label>
          <div class="style-picker" id="grave-style-picker" data-style="standard">
            ${HEADSTONE_STYLE_KEYS.map(
              (key) => `
              <button type="button" class="style-picker__btn${key === "standard" ? " is-selected" : ""}" data-style="${key}">
                <span class="style-picker__swatch style-picker__swatch--${key}"></span>
                <span class="style-picker__label">${t(styleLabelKey(key))}</span>
              </button>
            `,
            ).join("")}
          </div>
        </div>

        <div class="crea-field crea-photo-field">
          <label class="field-label" for="grave-photo-input">Fotografia <span class="field-optional">(opzionale)</span></label>
          <div class="crea-photo-upload" id="grave-photo-gate">
            ${renderUploadEmptyHtml()}
            <input type="file" id="grave-photo-input" class="field-input crea-file" accept="image/*">
          </div>
        </div>

        <button type="button" class="btn-gold crea-submit" onclick="proceedToPayment()">
          <i data-lucide="heart"></i>
          ${t("grave.create")}
        </button>
        <p class="crea-note">${t("grave.createNote")}</p>
      </form>

      <div id="crea-otp-panel" class="crea-otp" hidden>
        <div class="eyebrow">${t("auth.otpTitle")}</div>
        <p class="crea-otp__note" id="crea-otp-note"></p>
        <div class="crea-field">
          <label class="field-label" for="crea-otp">${t("auth.otpPlaceholder")} <span class="req">*</span></label>
          <input type="text" id="crea-otp" class="field-input" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" placeholder="${t("auth.otpPlaceholder")}">
        </div>
        <button type="button" class="btn-gold crea-submit" id="crea-otp-submit">
          <i data-lucide="heart"></i>
          ${t("auth.otpConfirm")}
        </button>
        <div class="crea-otp__actions">
          <button type="button" class="text-link-btn" id="crea-otp-back">${t("auth.otpBack")}</button>
          <button type="button" class="text-link-btn" id="crea-otp-resend">${t("auth.otpResend")}</button>
        </div>
      </div>

      <aside class="crea-preview">
        <div class="eyebrow crea-preview-label">Anteprima</div>
        <div class="crea-preview-card">
          <div class="crea-preview-photo" id="crea-preview-photo"></div>
          <div class="crea-preview-name" id="crea-preview-name">${t("view.namePlaceholder")}</div>
          <div class="crea-preview-epitaph" id="crea-preview-epitaph"></div>
        </div>
      </aside>
    </div>
    </div>
  `;

  creaViewMounted = true;
  bindCreaPreview();
  bindCreaStylePicker();
  bindCreaAuthStep();
  resetCreaPhotoPreview();
  syncCreaGuestAuth();
}

function getCreaPreviewSeed(): string {
  const name = (document.getElementById("grave-name") as HTMLInputElement | null)?.value.trim();
  return name || "crea-preview";
}

function resetCreaPhotoPreview(): void {
  const preview = document.getElementById("crea-preview-photo");
  const upload = document.querySelector(".crea-photo-upload .pet-photo-upload") as HTMLElement | null;
  const photo = document.getElementById("grave-photo-input") as HTMLInputElement | null;
  const seed = getCreaPreviewSeed();
  if (preview) renderCreaPreviewPhoto(preview, null, seed);
  if (upload) updateCreaPhotoUpload(upload, null, seed);
  if (photo) photo.value = "";
  updateCreaPreview();
}

function updateCreaPreview() {
  const name = (document.getElementById("grave-name") as HTMLInputElement | null)?.value.trim();
  const epitaph = (document.getElementById("grave-text") as HTMLTextAreaElement | null)?.value.trim();
  const nameEl = document.getElementById("crea-preview-name");
  const epEl = document.getElementById("crea-preview-epitaph");
  const preview = document.getElementById("crea-preview-photo");
  const photo = document.getElementById("grave-photo-input") as HTMLInputElement | null;
  const seed = getCreaPreviewSeed();
  if (nameEl) nameEl.textContent = name || t("view.namePlaceholder");
  if (epEl) epEl.textContent = epitaph ? `“${epitaph}”` : "";
  if (preview && !photo?.files?.[0]) renderCreaPreviewPhoto(preview, null, seed);
}

function bindCreaPreview() {
  const name = document.getElementById("grave-name");
  const text = document.getElementById("grave-text");
  const photo = document.getElementById("grave-photo-input") as HTMLInputElement | null;
  name?.addEventListener("input", updateCreaPreview);
  text?.addEventListener("input", updateCreaPreview);
  photo?.addEventListener("change", () => {
    if (!isPethernityActive() && photo.files?.[0]) {
      photo.value = "";
      openPaywall(t("pethernity.perkPhotos"));
      return;
    }
    const box = document.getElementById("crea-preview-photo");
    const upload = document.querySelector(".crea-photo-upload .pet-photo-upload") as HTMLElement | null;
    const file = photo.files?.[0] ?? null;
    const seed = getCreaPreviewSeed();
    if (box) renderCreaPreviewPhoto(box, file, seed);
    if (upload) updateCreaPhotoUpload(upload, file, seed);
  });
}

function selectedCreaStyle(): string {
  return document.getElementById("grave-style-picker")?.dataset.style || "standard";
}

function bindCreaStylePicker() {
  const picker = document.getElementById("grave-style-picker");
  if (!picker || picker.dataset.bound) return;
  picker.dataset.bound = "1";
  picker.querySelectorAll(".style-picker__btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const style = btn.getAttribute("data-style") || "standard";
      if (style !== "standard" && !isPethernityActive()) {
        openPaywall(t("pethernity.perkStyles"));
        return;
      }
      picker.dataset.style = style;
      picker.querySelectorAll(".style-picker__btn").forEach((el) => {
        el.classList.toggle("is-selected", el === btn);
      });
    });
  });
}

function setCreaOtpVisible(visible: boolean) {
  const form = document.getElementById("crea-form");
  const otp = document.getElementById("crea-otp-panel");
  if (form) form.hidden = visible;
  if (otp) otp.hidden = !visible;
  const note = document.getElementById("crea-otp-note");
  if (note && visible) {
    note.textContent = t("auth.otpSent", {
      email: pendingCreaRegisterEmail ?? "",
    });
  }
  if (visible && typeof lucide !== "undefined") lucide.createIcons();
}

function syncCreaGuestAuth() {
  const block = document.getElementById("crea-account-block");
  if (!block) return;
  const guest = !getCurrentUser();
  block.hidden = !guest || !!pendingCreaRegisterEmail;
  if (!pendingCreaRegisterEmail) setCreaOtpVisible(false);
}

function resetCreaAuthStep() {
  pendingCreaRegisterEmail = null;
  const email = document.getElementById("crea-email") as HTMLInputElement | null;
  const password = document.getElementById(
    "crea-password",
  ) as HTMLInputElement | null;
  const otp = document.getElementById("crea-otp") as HTMLInputElement | null;
  if (email) email.value = "";
  if (password) password.value = "";
  if (otp) otp.value = "";
  setCreaOtpVisible(false);
  syncCreaGuestAuth();
}

function bindCreaAuthStep() {
  const submit = document.getElementById("crea-otp-submit");
  const back = document.getElementById("crea-otp-back");
  const resend = document.getElementById("crea-otp-resend");
  submit?.addEventListener("click", () => {
    void verifyCreaRegistrationOtp();
  });
  back?.addEventListener("click", () => {
    pendingCreaRegisterEmail = null;
    setCreaOtpVisible(false);
    syncCreaGuestAuth();
  });
  resend?.addEventListener("click", () => {
    void resendCreaRegistrationOtp();
  });
}

async function verifyCreaRegistrationOtp() {
  const email = pendingCreaRegisterEmail;
  const otpInput = document.getElementById("crea-otp") as HTMLInputElement | null;
  const code = otpInput?.value.trim() ?? "";
  if (!email) {
    pendingCreaRegisterEmail = null;
    setCreaOtpVisible(false);
    syncCreaGuestAuth();
    return;
  }
  if (!/^\d{6}$/.test(code)) {
    showToast(t("auth.otpInvalid"));
    return;
  }
  setCheckoutBusy(true);
  try {
    await verifyRegisterOtp(email, code);
    pendingCreaRegisterEmail = null;
    setCreaOtpVisible(false);
    syncCreaGuestAuth();
    setCheckoutBusy(false);
    await proceedToPayment();
  } catch (err: any) {
    showToast(translateApiError(err) || t("auth.otpInvalid"));
    setCheckoutBusy(false);
  }
}

async function resendCreaRegistrationOtp() {
  const email = pendingCreaRegisterEmail;
  if (!email) return;
  try {
    await resendRegisterOtp(email);
    showToast(t("auth.otpResent"), "info");
  } catch (err: any) {
    showToast(translateApiError(err) || t("auth.authFailed"));
  }
}

function prepareCreaView() {
  ensureCreaView();
  ensureGraveFormReady();
  clearPetFormFields("grave");
  resetCreaPhotoPreview();
  resetCreaAuthStep();
  void dismissOnboarding({ animate: true });
}

// Coordinate conflicts mean the spot allocated on the client turned out to
// overlap/clash with a memorial the backend already knows about (e.g. a
// concurrent purchase, or a spot the client's cache hadn't caught). Retrying
// with the *same* spot would just fail again, so on these errors we pick a
// fresh random one automatically instead of leaving the user stuck.
const COORD_CONFLICT_MESSAGES = new Set([
  "Coordinates overlap an existing headstone",
  "Coordinates already occupied",
  "Coordinates are not allowed on the temple",
  "Coordinates are above the garden horizon",
]);

const MAX_CHECKOUT_ATTEMPTS = 3;

function holeIdToCoords(holeId: string): [number, number] {
  if (holeId.startsWith("home_")) {
    const map: Record<string, [number, number]> = {
      home_t1: [-9001, -9001],
      home_t2: [-9000, -9001],
      home_t3: [-8999, -9001],
      home_b1: [-9001, -9000],
      home_b2: [-9000, -9000],
      home_b3: [-8999, -9000],
    };
    return map[holeId] ?? [0, 0];
  }
  const [gx, gy] = holeId.split(",").map((v) => parseInt(v, 10));
  return [gx, gy];
}

function trackPurchaseView(holeId?: string) {
  trackMetaEvent("ViewContent", {
    content_ids: holeId ? [holeId] : undefined,
    content_type: "product",
    content_name: "Pethernity memorial",
    currency: META_CHECKOUT_CURRENCY,
    value: META_CHECKOUT_VALUE,
  });
}

// Open the Crea view for a specific empty hole (clicked in the garden).
export function openPurchaseModal(el) {
  mapState.selectedHole = el;
  prepareCreaView();
  showView("crea");
  trackPurchaseView(el?.dataset?.id);
}

// Sidebar CTA: open the Crea view immediately; hole allocation happens at checkout.
export function openCreateMemorial() {
  mapState.selectedHole = null;
  prepareCreaView();
  showView("crea");
  trackPurchaseView();
}

export function closeModal() {
  mapState.selectedHole = null;
  showView("garden");
}

export async function createGrave() {
  await proceedToPayment();
}

function getCheckoutSubmitButton(): HTMLButtonElement | null {
  const otp = document.getElementById("crea-otp-panel");
  if (otp && !otp.hidden) {
    return document.getElementById("crea-otp-submit") as HTMLButtonElement | null;
  }
  return document.querySelector(
    "#crea-form .crea-submit",
  ) as HTMLButtonElement | null;
}

function setCheckoutBusy(busy: boolean) {
  const btn = getCheckoutSubmitButton();
  if (!btn) return;
  btn.disabled = busy;
  btn.setAttribute("aria-busy", busy ? "true" : "false");
  if (busy) {
    btn.dataset.label = btn.dataset.label || btn.textContent || "";
    btn.innerHTML = `<i data-lucide="loader-circle"></i> ${t("grave.creating")}`;
    if (typeof lucide !== "undefined") lucide.createIcons();
  } else if (btn.dataset.label) {
    btn.innerHTML = `<i data-lucide="heart"></i> ${btn.dataset.label}`;
    if (typeof lucide !== "undefined") lucide.createIcons();
  }
}

export async function proceedToPayment() {
  const submitBtn = getCheckoutSubmitButton();
  if (submitBtn?.disabled) return;

  // Crea can be opened from the sidebar CTA without a pre-selected spot; allocate
  // the nearest empty one on demand.
  if (!mapState.selectedHole) {
    const hole = allocateEmptyHole();
    // allocateEmptyHole already surfaces map.noEmptyNearby when nothing can be reserved.
    if (!hole) return;
  }

  const f = readPetFormFields("grave");

  if (!f.name) {
    showToast(t("grave.nameRequired"));
    return;
  }
  if (!f.epitaph) {
    showToast(t("grave.epitaphRequired"));
    return;
  }
  const dateErr = validatePetDates(f.birthDate ?? "", f.deathDate ?? "");
  if (dateErr) {
    showToast(`⚠️ ${dateErr}`);
    return;
  }

  const user = await refreshSession();
  if (!user) {
    const email = (
      document.getElementById("crea-email") as HTMLInputElement | null
    )?.value.trim() ?? "";
    const password =
      (document.getElementById("crea-password") as HTMLInputElement | null)
        ?.value ?? "";
    if (!email || password.length < 6) {
      showToast(t("auth.invalidCredentials"));
      return;
    }
    setCheckoutBusy(true);
    try {
      await startRegister(email, password);
      pendingCreaRegisterEmail = email.toLowerCase();
      const passwordInput = document.getElementById(
        "crea-password",
      ) as HTMLInputElement | null;
      if (passwordInput) passwordInput.value = "";
      setCreaOtpVisible(true);
    } catch (err: any) {
      showToast(translateApiError(err) || t("auth.authFailed"));
    } finally {
      setCheckoutBusy(false);
    }
    return;
  }
  if (!user.emailVerified) {
    showToast(t("auth.verifyBeforePurchase"), "info", 7000);
    return;
  }

  setCheckoutBusy(true);

  const styleKey = selectedCreaStyle();
  if (styleKey !== "standard" && !isPethernityActive(user)) {
    setCheckoutBusy(false);
    openPaywall(t("pethernity.perkStyles"));
    return;
  }

  let imageObjectName: string | null = null;
  if (f.inpPhoto?.files && f.inpPhoto.files[0]) {
    if (!isPethernityActive(user)) {
      setCheckoutBusy(false);
      openPaywall(t("pethernity.perkPhotos"));
      return;
    }
    try {
      const blob = await compressImageToBlob(f.inpPhoto.files[0], 800, 0.8);
      const { objectName } = await uploadPetPhoto(blob);
      imageObjectName = objectName;
    } catch (e: any) {
      console.error("Photo upload error:", e);
      if (!handleEntitlementError(e, t("pethernity.perkPhotos"))) {
        showToast(t("grave.uploadFailed", { message: e?.message ?? e }));
      }
      setCheckoutBusy(false);
      return;
    }
  }

  let holeId = mapState.selectedHole?.dataset?.id as string | undefined;
  if (!holeId) {
    showToast(t("grave.spotUnavailable"), "info", 7000);
    setCheckoutBusy(false);
    return;
  }

  const pet: Record<string, string> = { name: f.name };
  if (f.species) pet.species = f.species;
  if (f.breed) pet.breed = f.breed;
  if (f.birthDate) pet.birthDate = f.birthDate;
  if (f.deathDate) pet.deathDate = f.deathDate;
  if (imageObjectName) {
    pet.imageObjectName = imageObjectName;
    pet.imageMime = "image/jpeg";
  }

  const triedHoleIds: string[] = [];

  for (let attempt = 1; attempt <= MAX_CHECKOUT_ATTEMPTS; attempt++) {
    const [x, y] = holeIdToCoords(holeId);

    try {
      const { data, error } = await api.POST("/headstones", {
        body: {
          x,
          y,
          epitaph: f.epitaph,
          styleKey,
          pet,
        },
      });

      if (error || !data) {
        const rawMessage = (error as any)?.message;

        if (
          rawMessage &&
          COORD_CONFLICT_MESSAGES.has(rawMessage) &&
          attempt < MAX_CHECKOUT_ATTEMPTS
        ) {
          triedHoleIds.push(holeId);
          mapState.selectedHole = null;
          const nextHole = allocateEmptyHole({ excludeIds: triedHoleIds });
          const nextHoleId = nextHole?.dataset?.id as string | undefined;
          if (!nextHoleId) {
            showToast(translateApiMessage(rawMessage));
            setCheckoutBusy(false);
            return;
          }
          holeId = nextHoleId;
          continue;
        }

        if (handleEntitlementError(error, t("pethernity.required"))) {
          setCheckoutBusy(false);
          return;
        }

        const msg =
          rawMessage === "Email verification required"
            ? t("auth.verifyBeforePurchase")
            : translateApiMessage(rawMessage) ?? t("grave.unexpectedError");
        showToast(`❌ ${msg}`);
        setCheckoutBusy(false);
        return;
      }

      applyHeadstone(data);
      setCheckoutBusy(false);
      mapState.selectedHole = null;
      clearPetFormFields("grave");
      resetCreaPhotoPreview();
      await openMemorialThanks({
        headstone: data,
        holeId,
        name: f.name,
        epitaph: f.epitaph,
        birthDate: f.birthDate,
        deathDate: f.deathDate,
      });
      return;
    } catch (err: any) {
      console.error("Create memorial failed", err);
      if (!handleEntitlementError(err)) {
        showToast(t("grave.checkoutError", { message: err?.message ?? err }));
      }
      setCheckoutBusy(false);
      return;
    }
  }
}
